(() => {
  'use strict';

  if (window.FoodNetSearchIntelligence) return;

  const nativeFetch = window.fetch.bind(window);
  const rootPath = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };
  const route = path => `${rootPath()}${String(path).replace(/^\//, '')}`;
  const clean = value => String(value ?? '').replace(/\s+/g, ' ').trim();
  const normalize = value => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[‐‑‒–—―]/g, '-')
    .replace(/[_.,/+()\[\]{}:;!?"']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const compact = value => normalize(value).replace(/[\s-]+/g, '');
  const isSkuLike = value => {
    const term = clean(value);
    if (!term || term.includes(' ')) return false;
    if (/^\d{3,}$/.test(term)) return true;
    return /\d/.test(term) && /^[a-z0-9._\/-]{4,}$/i.test(term);
  };

  const LEXICON = [
    'jasmine rice|jasmijnrijst,jasmijn rijst,riz jasmin,ข้าวหอมมะลิ',
    'sticky rice|glutinous rice,kleefrijst,plakrijst,riz gluant,ข้าวเหนียว',
    'sushi rice|sushirijst,sushi rijst,riz sushi,ข้าวซูชิ',
    'rice|rijst,riz,ข้าว',
    'rice noodles|rijstnoedels,rijst noodles,nouilles de riz,เส้นก๋วยเตี๋ยว,เส้นเล็ก',
    'glass noodles|glasnoedels,vermicelles mungo,วุ้นเส้น',
    'instant noodles|instantnoedels,nouilles instantanées,บะหมี่กึ่งสำเร็จรูป',
    'noodles|noedels,nouilles,บะหมี่,เส้น',
    'coconut milk|kokosmelk,kokos melk,lait de coco,กะทิ',
    'coconut cream|kokosroom,crème de coco,หัวกะทิ',
    'fish sauce|vissaus,vis saus,sauce poisson,น้ำปลา',
    'oyster sauce|oestersaus,oester saus,sauce huître,น้ำมันหอย',
    'dark soy sauce|donkere sojasaus,sauce soja foncée,ซีอิ๊วดำ',
    'light soy sauce|lichte sojasaus,sauce soja claire,ซีอิ๊วขาว',
    'sweet soy sauce|kecap manis,ketjap manis,zoete sojasaus,ซีอิ๊วหวาน',
    'soy sauce|sojasaus,soja saus,sauce soja,ซีอิ๊ว,ซอสถั่วเหลือง',
    'chilli sauce|chili sauce,chilisaus,sauce piment,ซอสพริก',
    'sriracha|siracha,ศรีราชา,ซอสศรีราชา',
    'curry paste|currypasta,pâte de curry,พริกแกง',
    'curry powder|kerriepoeder,poudre de curry,ผงกะหรี่',
    'sesame oil|sesamolie,huile de sésame,น้ำมันงา',
    'rice vinegar|rijstazijn,vinaigre de riz,น้ำส้มสายชูข้าว',
    'hoisin sauce|hoisinsaus,sauce hoisin,ซอสฮอยซิน',
    'tofu|tahoe,tofoe,เต้าหู้',
    'spring rolls|loempia,loempias,rouleaux de printemps,ปอเปี๊ยะ',
    'dumplings|dumpling,gyoza,raviolis asiatiques,เกี๊ยว',
    'kimchi|kim chi,กิมจิ',
    'tapioca starch|tapiocazetmeel,fécule de tapioca,แป้งมันสำปะหลัง',
    'tapioca pearls|boba,tapiocaparels,perles de tapioca,ไข่มุก',
    'rice flour|rijstmeel,farine de riz,แป้งข้าวเจ้า',
    'sticky rice flour|kleefrijstmeel,farine de riz gluant,แป้งข้าวเหนียว',
    'shrimp paste|garnalenpasta,pâte de crevettes,กะปิ',
    'tamarind|tamarinde,tamarin,มะขาม',
    'palm sugar|palmsuiker,sucre de palme,น้ำตาลปี๊บ',
    'miso|miso paste,pâte miso,มิโสะ',
    'nori|seaweed,zeewier,algue nori,สาหร่ายโนริ',
    'wasabi|วาซาบิ',
    'chicken|kip,poulet,ไก่',
    'beef|rundvlees,boeuf,เนื้อวัว',
    'pork|varkensvlees,porc,หมู',
    'shrimp|garnalen,crevettes,กุ้ง',
    'duck|eend,canard,เป็ด',
    'frozen|bevroren,surgelé,surgelés,แช่แข็ง',
    'drinks|dranken,boissons,เครื่องดื่ม',
    'snacks|snack,hapjes,ขนม',
    'spices|kruiden,épices,เครื่องเทศ',
    'canned|conserven,conserves,กระป๋อง',
    'Aroy-D|aroy d,aroyd,อร่อยดี',
    'Healthy Boy|healthyboy,เด็กสมบูรณ์',
    'Samyang|sam yang,ซัมยัง',
    'Sunlee|sun lee,ซันลี'
  ];

  const groups = LEXICON.map(row => {
    const [primary, aliases = ''] = row.split('|');
    return { primary, aliases: [primary, ...aliases.split(',').filter(Boolean)] };
  });
  const entries = [];
  const exact = new Map();
  groups.forEach(group => group.aliases.forEach(alias => {
    const key = normalize(alias);
    const tight = compact(alias);
    const entry = { group, key, tight };
    entries.push(entry);
    if (key) exact.set(key, group);
    if (tight) exact.set(tight, group);
  }));
  entries.sort((a, b) => b.key.length - a.key.length);

  const distance = (a, b, cutoff) => {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > cutoff) return cutoff + 1;
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
      const current = [i];
      let rowMin = i;
      for (let j = 1; j <= b.length; j += 1) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
        rowMin = Math.min(rowMin, current[j]);
      }
      if (rowMin > cutoff) return cutoff + 1;
      previous = current;
    }
    return previous[b.length];
  };

  const findGroup = raw => {
    const key = normalize(raw);
    const tight = compact(raw);
    if (!key) return { group: null, reason: '' };

    const direct = exact.get(key) || exact.get(tight);
    if (direct) return { group: direct, reason: normalize(direct.primary) === key ? '' : 'language' };

    const contained = entries.find(entry => entry.key.length >= 6 && (key.includes(entry.key) || tight.includes(entry.tight)));
    if (contained) return { group: contained.group, reason: 'language' };

    if (!/^[a-z0-9]+$/.test(tight) || tight.length < 4 || tight.length > 28) return { group: null, reason: '' };
    const cutoff = tight.length <= 5 ? 1 : 2;
    const best = new Map();
    entries.forEach(entry => {
      if (!/^[a-z0-9]+$/.test(entry.tight) || Math.abs(entry.tight.length - tight.length) > cutoff) return;
      const score = distance(tight, entry.tight, cutoff);
      if (score > cutoff) return;
      const current = best.get(entry.group);
      if (!current || score < current.score) best.set(entry.group, { score, length: entry.tight.length });
    });
    const ranked = [...best.entries()]
      .map(([group, match]) => ({ group, ...match }))
      .sort((a, b) => a.score - b.score || b.length - a.length);
    if (!ranked.length || (ranked[1] && ranked[1].score === ranked[0].score)) return { group: null, reason: '' };
    return { group: ranked[0].group, reason: 'typo' };
  };

  const resolve = raw => {
    const original = clean(raw);
    if (!original || isSkuLike(original)) return { raw: original, candidates: original ? [original] : [], bestGuess: original, reason: '' };
    const found = findGroup(original);
    const candidates = [];
    const seen = new Set();
    const add = value => {
      const term = clean(value);
      const key = normalize(term);
      if (!term || seen.has(key)) return;
      seen.add(key);
      candidates.push(term);
    };
    add(original);
    if (found.group) {
      add(found.group.primary);
      found.group.aliases.forEach(alias => candidates.length < 5 && add(alias));
    }
    return { raw: original, candidates, bestGuess: found.group?.primary || original, reason: found.reason };
  };

  const predictiveUrl = (term, limit = 1) => {
    const params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(limit));
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][prefix]', 'last');
    return `${route('search/suggest.json')}?${params}`;
  };

  const findBestTerm = async (raw, signal) => {
    const resolution = resolve(raw);
    if (resolution.candidates.length < 2 || isSkuLike(raw)) return resolution.raw;
    for (const candidate of resolution.candidates.slice(0, 4)) {
      try {
        const response = await nativeFetch(predictiveUrl(candidate), {
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          signal
        });
        if (!response.ok) continue;
        const payload = await response.json();
        if ((payload?.resources?.results?.products || []).length) return candidate;
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
      }
    }
    return resolution.raw;
  };

  window.FoodNetSearchIntelligence = Object.freeze({ normalize, isSkuLike, resolve, findBestTerm });
  document.dispatchEvent(new CustomEvent('foodnet:search-intelligence-ready'));

  const itemKey = item => String(item?.id || item?.url || item?.handle || item?.title || '');
  const mergeUnique = (base, extra) => {
    const seen = new Set(base.map(itemKey));
    extra.forEach(item => {
      const key = itemKey(item);
      if (!key || seen.has(key)) return;
      seen.add(key);
      base.push(item);
    });
  };

  window.fetch = async function foodNetSmartFetch(input, init) {
    const requestUrl = input instanceof Request ? input.url : String(input);
    let url;
    try { url = new URL(requestUrl, window.location.origin); } catch { return nativeFetch(input, init); }
    if (!url.pathname.endsWith('/search/suggest.json')) return nativeFetch(input, init);

    const raw = clean(url.searchParams.get('q'));
    const resolution = resolve(raw);
    if (!resolution.reason || resolution.candidates.length < 2 || isSkuLike(raw)) return nativeFetch(input, init);

    const firstResponse = await nativeFetch(input, init);
    if (!firstResponse.ok) return firstResponse;

    let firstPayload;
    try { firstPayload = await firstResponse.clone().json(); } catch { return firstResponse; }
    const firstResults = firstPayload?.resources?.results || {};
    if ((firstResults.products || []).length >= 4) return firstResponse;

    const products = [...(firstResults.products || [])];
    const collections = [...(firstResults.collections || [])];
    let usedFallback = false;

    for (const candidate of resolution.candidates.slice(1, 4)) {
      if (init?.signal?.aborted) return firstResponse;
      const alternateUrl = new URL(url.toString());
      alternateUrl.searchParams.set('q', candidate);
      try {
        const response = await nativeFetch(alternateUrl.toString(), init);
        if (!response.ok) continue;
        const payload = await response.json();
        const found = payload?.resources?.results || {};
        const before = products.length;
        mergeUnique(products, found.products || []);
        mergeUnique(collections, found.collections || []);
        usedFallback ||= products.length > before;
        if (products.length >= 6) break;
      } catch (error) {
        if (error?.name === 'AbortError') return firstResponse;
      }
    }

    if (!usedFallback) return firstResponse;
    const merged = typeof structuredClone === 'function' ? structuredClone(firstPayload) : JSON.parse(JSON.stringify(firstPayload));
    merged.resources = merged.resources || {};
    merged.resources.results = merged.resources.results || {};
    merged.resources.results.products = products;
    merged.resources.results.collections = collections;
    return new Response(JSON.stringify(merged), {
      status: firstResponse.status,
      statusText: firstResponse.statusText,
      headers: { 'Content-Type': 'application/json; charset=utf-8' }
    });
  };

  const ensurePrefix = form => {
    if (form.querySelector('input[name="options[prefix]"]')) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'options[prefix]';
    input.value = 'last';
    form.append(input);
  };

  const navigate = (form, term) => {
    const url = new URL(form.action || route('search'), window.location.origin);
    new FormData(form).forEach((value, key) => url.searchParams.set(key, String(value)));
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    window.location.assign(url.toString());
  };

  document.querySelectorAll('[data-enterprise-search-form]').forEach(ensurePrefix);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll?.('[data-enterprise-search-form]').forEach(ensurePrefix);
  });

  document.addEventListener('submit', event => {
    const form = event.target.closest?.('[data-enterprise-search-form]');
    if (!form) return;
    const input = form.querySelector('[data-enterprise-search-input]');
    const raw = clean(input?.value);
    const resolution = resolve(raw);
    if (!raw || !resolution.reason || resolution.candidates.length < 2 || isSkuLike(raw)) return;

    event.preventDefault();
    const controller = new AbortController();
    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    form.setAttribute('aria-busy', 'true');

    findBestTerm(raw, controller.signal)
      .then(best => navigate(form, best || raw))
      .catch(() => navigate(form, raw))
      .finally(() => {
        form.removeAttribute('aria-busy');
        if (button) button.disabled = false;
      });
  }, true);

  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-enterprise-search-view-all]');
    if (!link) return;
    const root = link.closest('[data-enterprise-search]');
    const form = root?.querySelector('[data-enterprise-search-form]');
    const input = root?.querySelector('[data-enterprise-search-input]');
    const raw = clean(input?.value);
    const resolution = resolve(raw);
    if (!form || !raw || !resolution.reason || resolution.candidates.length < 2 || isSkuLike(raw)) return;

    event.preventDefault();
    findBestTerm(raw).then(best => navigate(form, best || raw)).catch(() => navigate(form, raw));
  }, true);
})();
